/**
 * IcgCompositionExplainer — explainer for ICG-De, the COMPOSITION Immunity
 * Closure Guarantee (governance name: COMPOSITION Safety Envelope).
 *
 * ICG-De pairs the COMPOSE Neutralization Theorem (primal) with the
 * DECOMPOSE Structural Duality Principle (dual) along the operad ⊣
 * cooperad bar–cobar adjunction Ω ⊣ B. The Koszul resolution ΩBO ≃ O is
 * the structural proof that assembly and extraction are mutually inverse
 * up to coherent equivalence.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AuditSurfaceDelta,
  CalloutBox,
  CounterModelGrid,
  ExplainerCard,
  SafetyEnvelopeDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const PRIMAL_ACCENT = THEOREM_COLOR.COMPOSE;
const DUAL_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.DECOMPOSE.color;

const COUNTER_MODELS = [
  {
    title: "Primal only — COMPOSE, no DECOMPOSE",
    body: "Assembling subsystems preserves safety inside one regime, but extracting a subsystem is unwitnessed: a decomposition can drop the dependency record, so an extracted component looks self-contained when it is not — and a later recomposition re-introduces an ambient dependency the audit will not catch.",
  },
  {
    title: "Dual only — DECOMPOSE, no COMPOSE",
    body: "The operad ⊣ cooperad duality makes extraction declare its dependencies, but with no composition neutralization there is no safe assembly to recover — DECOMPOSE certifies the inverse of an empty guarantee.",
  },
] as const;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-icg-de",
  kind: "SSM · Immunity Closure Guarantee (S1⊗S2)",
  title: "ICG-De",
  subtitle: "COMPOSITION Safety Envelope · COMPOSE ⊗ DECOMPOSE",
  accent: PRIMAL_ACCENT,
  accentAlt: DUAL_ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "COMPOSE (primal NT)", href: "#explainer-compose", accent: PRIMAL_ACCENT },
  { label: "DECOMPOSE (dual SDP)", href: "#explainer-decompose", accent: DUAL_ACCENT },
  { label: "Operad", href: "#explainer-operad" },
  { label: "Cooperad", href: "#explainer-cooperad" },
  { label: "Bioperad", href: "#explainer-bioperad" },
];

const ENVELOPE = (
  <SafetyEnvelopeDiagram
    icg="ICG-De"
    primalLabel="COMPOSE"
    primalKind="Neutralization Theorem · C"
    primalAccent={PRIMAL_ACCENT}
    dualLabel="DECOMPOSE"
    dualKind="Structural Duality Principle · De"
    dualAccent={DUAL_ACCENT}
    adjunction="operad ⊣ cooperad (Ω ⊣ B)"
    unitRole="bar — declare dependencies"
    counitRole="cobar — recover assembly"
  />
);

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ICG-De</strong> is the COMPOSITION Safety Envelope. It seals composition
      neutralization (COMPOSE) and its compositional dual (DECOMPOSE) into one two-sided
      certificate, so a system can be taken apart and reassembled with no safety drift.
    </p>
  ),
  intuition: (
    <>
      <p>
        COMPOSE makes hidden-dependency assembly unconstructable: an operad's operations are the
        typed ways of plugging components together. DECOMPOSE certifies that the dual cooperad makes
        every extraction declare its residual dependencies.
      </p>
      <p>
        Held separately, the composition theorem and the decomposition principle are sound about
        different shapes. The coherence "any decomposition of a safely composed system is itself
        safe, and any safe composition can be decomposed without losing safety information" is an
        external argument.
      </p>
      <p>
        ICG-De is the single Koszul dual pair that contains both sides. Its bar and cobar maps,
        sealed by the Koszul resolution, are the structural proof that assembly and extraction are
        complementary — the envelope is <strong>strictly stronger</strong> than holding the theorem
        and the principle apart.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ICG-De is the Koszul dual pair: an operad <MathInline>{tex`O`}</MathInline> (safe
        composition) and its dual cooperad <code>O¡</code> (safe extraction), joined by the
        bar–cobar adjunction <MathInline>{tex`\Omega \dashv B`}</MathInline>. Its structural
        witnesses are:
      </p>
      <ul>
        <li>
          the <strong>unit η</strong> — the bar map <MathInline>{tex`B`}</MathInline>: it builds the
          cooperad of ways to take a composite apart, declaring residual dependencies;
        </li>
        <li>
          the <strong>counit ε</strong> — the cobar map <MathInline>{tex`\Omega`}</MathInline>: the
          Koszul resolution <MathInline>{tex`\Omega B O \simeq O`}</MathInline> recovers the operad
          from its dual.
        </li>
      </ul>
      <p>
        The triangle identities make assembly and extraction mutually inverse up to coherent
        equivalence: no decomposition loses a safety obligation; no recomposition smuggles one back
        in.
      </p>
      <AuditSurfaceDelta
        primalCheck="COMPOSE witness"
        dualCheck="DECOMPOSE witness"
        envelopeLabel="ICG-De envelope"
        primalAccent={PRIMAL_ACCENT}
        dualAccent={DUAL_ACCENT}
      />
      <p style={{ marginTop: "0.5rem" }}>
        Categorical form: ICG-De is the <em>Adjunction Certificate</em> whose left adjoint is the
        cobar (assembly) and whose right adjoint is the bar (extraction) — the Koszul resolution is
        the certificate that the two are mutual sections.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Each side alone admits a counter-model the
        envelope rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="ICG-De counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        ICG-De makes the forbidden generator <strong>G1 (ambient authority)</strong> structurally
        absent: the operad types every input and the cooperad declares every extraction, so an
        undeclared dependency in a composite is unconstructable. The envelope enforces security
        axiom A1, No Ambient Authority.
      </p>
      <p>
        <strong>Worked adversary.</strong> A plugin or subsystem whose ambient dependency is
        invisible at the composite boundary; a refactor or extraction that silently loses a safety
        obligation. Under ICG-De the bar map forces every extraction to surface its residual
        dependencies and the cobar map's Koszul resolution forbids reassembly without them — the
        composition-laundering attack is structurally inexpressible.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing ICG-De means bypassing COMPOSE and
        DECOMPOSE at once, or escaping the operad carriers — both leave the kernel category and
        vacate the guarantee rather than violate it.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-De" accent={DUAL_ACCENT}>
        COMPOSE (NT) ⊗ DECOMPOSE (SDP) = ICG-De. The audit surface collapses from an NT witness plus
        an SDP witness to one envelope carrying the bar–cobar unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Immunity Closure Guarantees (§7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "RD-97 — Immunity Closure Guarantees",
      path: "research/settled/RD-97-BI-GUARANTEES-TWO-SIDED-SAFETY.md",
      note: "Canonical source for the seven-envelope algebra and the naming triad.",
    },
    {
      label: "Safety Envelope foundations",
      path: "theory/structural/Immunity-Closure-Guarantees/00-Safety-Envelope-Foundations.md",
    },
    {
      label: "ICG-De theory document",
      path: "theory/structural/Immunity-Closure-Guarantees/06-ICG-De-Compose-And-Decompose.md",
    },
    {
      label: "Immunity Closure Guarantee (Agda)",
      path: "agda/Research/BiGuarantee/BiComposition.agda",
    },
  ],
};

export function IcgCompositionExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={ENVELOPE} crossLinks={CROSS_LINKS} />
  );
}
